import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SkipForward, Sparkles } from "lucide-react";
import { usePersonaSetup, trackPersonaSetup } from "../hooks/use-persona-setup";
import { QuestRail } from "./QuestRail";
import { GenderQuest } from "../steps/GenderQuest";
import { FitQuest } from "../steps/FitQuest";
import { RegionQuest } from "../steps/RegionQuest";
import { PhoneQuest } from "../steps/PhoneQuest";
import { TryOnQuest } from "../steps/TryOnQuest";

const QUESTS = {
  gender: GenderQuest,
  fit: FitQuest,
  region: RegionQuest,
  phone: PhoneQuest,
  tryOn: TryOnQuest,
} as const;

export const PersonaSetupPage: React.FC = () => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const { activeQuest, setActiveQuest, completeness, skip, leave } =
    usePersonaSetup();
  const Active = QUESTS[activeQuest];
  const viewedRef = useRef(false);
  const seededRef = useRef(false);
  // Sayfada gerçekten eksik quest görüldü mü? (Zaten tam gelen kullanıcı
  // için "finished" eventi ve kutlama beklemesi atılmasın.)
  const wasIncompleteRef = useRef(false);

  useEffect(() => {
    // Veriler yüklenmeden event atma; yoksa completed/percent hep 0 gider.
    if (viewedRef.current || completeness.isLoading) return;
    viewedRef.current = true;
    trackPersonaSetup("persona_setup_viewed", {
      completed: completeness.completedCount,
      percent: completeness.percent,
    });
  }, [completeness.isLoading, completeness.completedCount, completeness.percent]);

  useEffect(() => {
    if (seededRef.current || completeness.isLoading) return;
    seededRef.current = true;
    if (completeness.firstIncomplete) {
      setActiveQuest(completeness.firstIncomplete);
    }
  }, [completeness.firstIncomplete, completeness.isLoading, setActiveQuest]);

  useEffect(() => {
    if (completeness.isLoading) return;
    if (!completeness.isComplete) {
      wasIncompleteRef.current = true;
      return;
    }
    if (wasIncompleteRef.current) {
      trackPersonaSetup("persona_setup_finished", { percent: 100 });
      const timer = window.setTimeout(leave, 1400);
      return () => window.clearTimeout(timer);
    }
    // Sayfaya zaten tam gelinmiş: kutlamasız hemen çık.
    leave();
  }, [completeness.isLoading, completeness.isComplete, leave]);

  return (
    <div className="relative h-dvh w-full overflow-hidden bg-bg-primary dark:bg-gray-950">
      <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-indigo-400/25 blur-3xl motion-safe:animate-pulse dark:bg-indigo-500/20" />
      <div className="pointer-events-none absolute -bottom-28 -right-16 h-80 w-80 rounded-full bg-emerald-400/20 blur-3xl dark:bg-emerald-500/15" />
      <div
        className="pointer-events-none absolute inset-0 opacity-20 dark:opacity-10"
        style={{
          backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }}
      />

      <div className="relative z-10 flex h-full flex-col overflow-y-auto">
        <header className="flex items-center justify-between gap-3 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6 sm:py-4 md:px-10">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-text-brand dark:bg-indigo-500/20 dark:text-indigo-300 sm:h-9 sm:w-9">
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-base font-bold text-text-primary dark:text-gray-100 sm:text-lg">
                {t("personaSetup.title")}
              </h1>
              <p className="hidden text-xs text-text-tertiary dark:text-gray-400 sm:block">
                {t("personaSetup.subtitle")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={skip}
            className="group relative shrink-0 overflow-hidden rounded-full p-px shadow-[0_0_20px_-6px_rgba(99,102,241,0.7)] transition-transform active:scale-[0.97]"
          >
            <span
              aria-hidden
              className="absolute inset-0 bg-gradient-to-r from-indigo-400 via-violet-400 to-emerald-400 dark:from-indigo-400 dark:via-violet-400 dark:to-emerald-400"
            />
            <span className="relative flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-sm font-semibold text-text-primary dark:bg-gray-950 dark:text-gray-100 sm:px-4 sm:py-2">
              <SkipForward className="h-3.5 w-3.5 text-text-brand dark:text-indigo-300" />
              {t("personaSetup.skip")}
            </span>
          </button>
        </header>

        <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-4 px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:gap-6 sm:px-6 md:flex-row md:items-start md:gap-10 md:px-10">
          <div className="md:sticky md:top-6 md:w-72 md:shrink-0">
            <QuestRail />
          </div>

          <div className="min-w-0 flex-1 pb-4 md:pb-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeQuest}
                initial={reduceMotion ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, y: -12 }}
                transition={{ duration: reduceMotion ? 0 : 0.28 }}
              >
                {completeness.isComplete ? (
                  <div className="rounded-3xl border border-emerald-400/40 bg-white p-8 text-center shadow-lg dark:bg-gray-800">
                    <p className="text-2xl font-extrabold text-text-primary dark:text-gray-100">
                      {t("personaSetup.finishedTitle")}
                    </p>
                    <p className="mt-2 text-sm text-text-tertiary dark:text-gray-400">
                      {t("personaSetup.finishedSubtitle")}
                    </p>
                  </div>
                ) : (
                  <Active />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};
